import { Directive, EventEmitter, Injectable, ViewChild } from "@angular/core";

import { ProjectData } from "src/app/services/response-models/projects.response.model";
import { Select, Store } from "@ngxs/store";
import { ProjectsState } from "src/store/projects/projects.state";
import { AppState } from "src/store/app/app.state";
import { firstValueFrom, Observable } from "rxjs";
import { CompanySettings } from "src/app/services/response-models/companies.response.model";
import { UsersService } from "src/app/services/users.service";
import { ToastService } from "src/app/services/toast.service";
import { AlertController, ModalController } from "@ionic/angular";
import { FormsService } from "src/app/services/forms.service";
import { ProjectsService } from "src/app/services/projects.service";
import { TranslateService } from "@ngx-translate/core";
import { Navigate } from "src/store/router/router.actions";
import { Geolocation, PositionOptions } from "@capacitor/geolocation";
import { DateTime } from "src/app/employee/forms/form-field-types/date-fields/DateTime";
import { ConfigState } from "src/store/config/config.state";
import { ClockingRecord } from "src/app/services/response-models/user.response.model";
import { SetSearchBarStatus } from "src/store/projects/actions/set-search-bar.action";
import { LoadingComponent } from "src/app/shared/loading/loading.component";
import { sentryCaptureException } from "src/app/utility/errors";
import { CheckInOutModalComponent } from "src/app/employee/projects/check-in-out/check-in-out-modal/check-in-out-modal.component";
import { SettingsBooleanValues } from "src/app/shared/enums/settings.enums";
import { IonFab } from "@ionic/angular";

@Directive()
@Injectable({
    providedIn: "root",
})
export class UserCheckIn extends DateTime {
    @ViewChild("loading") loading: LoadingComponent;
    @ViewChild("backdrop") backdrop: any;
    @ViewChild("fab") fab: IonFab;

    @Select(ProjectsState.getSelectedProject) selectedProject$: Observable<ProjectData>;
    @Select(AppState.getIsUserCheckedIn) isCheckIn$: Observable<boolean>;

    public selectedProject: ProjectData;

    public userLocation: any;
    public isCheckedIn = false;
    public companySettings: CompanySettings;
    public isBackdropShown = false;
    public hasDetails: boolean;
    public isProjectOpen: boolean = null;

    private locationOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 4000,
        maximumAge: 1000,
    };

    constructor(
        public store: Store,
        public userService: UsersService,
        public toastService: ToastService,
        public alert: AlertController,
        public formService: FormsService,
        public projectService: ProjectsService,
        public translateService: TranslateService,
        public modalController: ModalController
    ) {
        super();
    }

    public setProject(): void {
        this.selectedProject$
            .subscribe((data) => {
                const hasAnyCustomField = data?.project_custom_field_values?.some((x) => !!x.value);
                this.projectService.isProjectOpen(data.id).then((resp) => {
                    this.isProjectOpen = resp;
                });
                if (
                    !data.contact &&
                    !data.street_name &&
                    !data.description &&
                    !hasAnyCustomField &&
                    !data.label_id
                ) {
                    this.hasDetails = false;
                    this.selectedProject = data;
                    return;
                }

                this.hasDetails = true;
                this.selectedProject = data;
            })
            .unsubscribe();
    }

    // Check in

    public checkInUser = (time: Date = null) => {
        this.loading?.startLoading();
        this.userService
            .checkIn(
                this.selectedProject.id,
                this.userLocation.latitude,
                this.userLocation.longitude,
                time
            )
            .then((resp) => {
                this.userService.isUserCheckedIn();
                this.isCheckedIn = true;
                this.loading?.endLoading();
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_IN_MESSAGE");
            })
            .catch((err) => {
                this.loading?.endLoading();
                sentryCaptureException(err);
                this.toastService.showGeneralErrorMessage();
            });
    };

    public setIsCheckedIn = async () => {
        this.isCheckIn$.subscribe((data) => {
            this.isCheckedIn = data;
        });
    };

    public checkOutUser = async (time: Date = null) => {
        this.loading?.startLoading();
        if (!this.companySettings) {
            this.companySettings = this.store.selectSnapshot(AppState.getCompanySettings);
        }

        if (this.companySettings.show_form_edit_on_check_out === SettingsBooleanValues.IS_ENABLED) {
            this.showCheckoutFormTemplate(time);
            return;
        }
        this.loading?.startLoading();

        this.userService
            .checkOut(this.userLocation.latitude, this.userLocation.longitude, time)
            .then((resp) => {
                this.userService.isUserCheckedIn();
                this.isCheckedIn = false;
                this.loading?.endLoading();
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_OUT_MESSAGE");
            })
            .catch((err) => {
                this.loading?.endLoading();
                sentryCaptureException(err);
                this.toastService.showGeneralErrorMessage();
            });
    };

    public showCheckoutFormTemplate = async (time: Date = null) => {
        this.loading?.startLoading();
        this.userService
            .isUserCheckedIn()
            .then((clocking_record: ClockingRecord) => {
                this.loading?.startLoading();
                this.userService
                    .checkOut(this.userLocation.latitude, this.userLocation.longitude, time)
                    .then(() => {
                        this.loading?.endLoading();
                        this.userService.isUserCheckedIn().then(() => {
                            this.setCheckoutFormTemplate(clocking_record.form_id);
                        });
                        this.isCheckedIn = false;
                        this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_OUT_MESSAGE");
                    })
                    .catch((err) => {
                        this.loading?.endLoading();
                        sentryCaptureException(err);
                        this.toastService.showGeneralErrorMessage();
                    });
            })
            .catch((err) => {
                this.loading?.endLoading();
                sentryCaptureException(err);
                this.toastService.showGeneralErrorMessage();
            });
    };

    public setCheckoutFormTemplate = async (formId: string) => {
        this.formService
            .getFormById(formId)
            .then(async (resp) => {
                await this.formService.getFormTemplateById(resp.form_template_id);
                await this.projectService.getProjectById(resp.project_id);
                this.store.dispatch(
                    new Navigate(["employee", "forms", "edit", "checkout", formId])
                );
            })
            .catch((err) => {
                sentryCaptureException(err);
                this.toastService.showGeneralErrorMessage();
            });
    };

    // Events
    public navigateToFormTemplates = async (trigger: string = "button") => {
        this.hideBackdrop();
        if (trigger === "label") {
            await this.fab?.close();
        }
        this.store.dispatch(new SetSearchBarStatus(true));
        this.store.dispatch(new Navigate(["employee", "forms", "list"]));
    };

    public navigateToCreateWallPost = async (event) => {
        this.hideBackdrop();
        this.store.dispatch(new Navigate(["employee", "projects", "wall", "create"]));
    };

    public checkIn = async (trigger: string = "button") => {
        this.loading?.startLoading();
        this.hideBackdrop();
        if (trigger === "label") {
            await this.fab?.close();
        }
        const platform = await firstValueFrom(this.store.selectOnce(ConfigState.getPlatform));
        let isAlertShown: boolean;

        // There seemts to be a differance between
        // Plugins.Geolocation and just Geolocation for iOS
        // We have to check the version
        if (platform === "ios") {
            const id = await Geolocation.watchPosition(
                this.locationOptions,
                async (position, err) => {
                    if (err) {
                        await Geolocation.clearWatch({ id });
                        this.toastService.showToastMessage("TOAST.LOCATION_TURNED_ON");
                        this.loading?.endLoading();
                        return;
                    }

                    this.userLocation = position.coords;

                    await Geolocation.clearWatch({ id });

                    if (!isAlertShown) {
                        isAlertShown = true;
                        await this.openCheckInOutModal(
                            this.userService.getGoogleStaticMapUrl(this.userLocation),
                            "checkInUser"
                        );
                    }
                    this.loading?.endLoading();
                }
            );
            return;
        }

        const permissionResult = await Geolocation.checkPermissions();

        if (permissionResult.location !== "granted") {
            try {
                await Geolocation.requestPermissions({ permissions: ["location"] });
            } catch (err) {
                sentryCaptureException(err);
                this.toastService.showToastMessage("TOAST.LOCATION_TURNED_ON");
                this.loading?.endLoading();
                return;
            }
        }

        const id = await Geolocation.watchPosition(this.locationOptions, async (position, err) => {
            if (err) {
                await Geolocation.clearWatch({ id });
                this.toastService.showToastMessage("TOAST.LOCATION_TURNED_ON");
                this.loading?.endLoading();
                return;
            }

            this.userLocation = position.coords;

            await Geolocation.clearWatch({ id });

            if (!isAlertShown) {
                isAlertShown = true;
                await this.openCheckInOutModal(
                    this.userService.getGoogleStaticMapUrl(this.userLocation),
                    "checkInUser"
                );
            }
            this.loading?.endLoading();
        });
    };

    public checkOut = async (trigger: string = "button") => {
        this.loading?.startLoading();
        this.hideBackdrop();
        if (trigger === "label") {
            await this.fab?.close();
        }
        const platform = await firstValueFrom(this.store.selectOnce(ConfigState.getPlatform));
        let isAlertShown: boolean;

        // There seemts to be a differance between
        // Plugins.Geolocation and just Geolocation for iOS
        // We have to check the version
        if (platform === "ios") {
            const id = await Geolocation.watchPosition(
                this.locationOptions,
                async (position, err) => {
                    if (err) {
                        await Geolocation.clearWatch({ id });
                        this.toastService.showToastMessage("TOAST.LOCATION_TURNED_ON");
                        this.loading?.endLoading();
                        return;
                    }

                    this.userLocation = position.coords;

                    await Geolocation.clearWatch({ id });

                    if (!isAlertShown) {
                        isAlertShown = true;
                        await this.openCheckInOutModal(
                            this.userService.getGoogleStaticMapUrl(this.userLocation),
                            "checkOutUser"
                        );
                    }
                    this.loading?.endLoading();
                }
            );
            return;
        }

        const id = await Geolocation.watchPosition(this.locationOptions, async (position, err) => {
            if (err) {
                await Geolocation.clearWatch({ id });
                this.toastService.showToastMessage("TOAST.LOCATION_TURNED_ON");
                this.loading?.endLoading();
                return;
            }

            this.userLocation = position.coords;

            await Geolocation.clearWatch({ id });

            if (!isAlertShown) {
                isAlertShown = true;
                await this.openCheckInOutModal(
                    this.userService.getGoogleStaticMapUrl(this.userLocation),
                    "checkOutUser"
                );
            }
            this.loading?.endLoading();
        });
    };

    public async openCheckInOutModal(mapUrl: string, type: string): Promise<void> {
        if (!this.companySettings) {
            this.companySettings = this.store.selectSnapshot(AppState.getCompanySettings);
        }

        const checkAtSpecificTime =
            this.companySettings.check_at_specific_time === SettingsBooleanValues.IS_ENABLED;

        const modal = await this.modalController.create({
            component: CheckInOutModalComponent,
            componentProps: {
                mapUrl,
                type,
                checkAtSpecificTime,
                onCheckInOut: new EventEmitter<{ type: string; time: Date | null }>(),
            },
            cssClass: "modal-fullscreen",
            keyboardClose: true,
            showBackdrop: true,
        });

        return await modal.present().then(() => {
            modal.componentProps.onCheckInOut.subscribe({
                next: (data) => {
                    this[`${data.type}`](data.time);
                },
            });
        });
    }

    public toggleBackdrop = async () => {
        if (this.isBackdropShown) {
            this.hideBackdrop();
            return;
        }

        this.showBackdrop();

        if (!(await this.projectService.isProjectOpen(this.selectedProject.id))) {
            // if project is closed hide backdrop and show message
            // do not redirect to projects list
            this.toastService.showToastMessage("TOAST.CANNOT_USE_PROJECT");
            this.hideBackdrop();
            return;
        }
    };

    public showBackdrop = () => {
        if (this.backdrop?.el) {
            this.backdrop.el.style.opacity = "0.7";
            this.backdrop.el.style["z-index"] = "12";
        }
        this.isBackdropShown = true;
    };

    public hideBackdrop = () => {
        if (this.backdrop?.el) {
            this.backdrop.el.style.opacity = "0";
            this.backdrop.el.style["z-index"] = "-1";
        }
        this.isBackdropShown = false;
    };
}
